// Polar/arc math for gauges and the compass rose (no DOM, unit-tested).
// Convention everywhere: angles in degrees, 0° = up (north), clockwise
// positive — the marine reading of every dial this library draws.

export interface Point {
  x: number;
  y: number;
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Point at radius r and bearing-style angle (0 up, clockwise). */
export function polarXY(cx: number, cy: number, r: number, angleDeg: number): Point {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** Normalize to [0, 360). */
export function normalizeAngle(a: number): number {
  const m = a % 360;
  return m < 0 ? m + 360 : m;
}

/** Shortest signed rotation from one bearing to another, in (-180, 180]. */
export function signedDelta(from: number, to: number): number {
  let d = normalizeAngle(to) - normalizeAngle(from);
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}

/**
 * Continue an unbounded rotation with the shortest turn toward the new
 * bearing. Feeding a CSS-transitioned `rotate()` this value makes a needle
 * crossing 359°→1° sweep 2°, not spin back through 358°.
 */
export function unwindAngle(prevContinuous: number, nextBearing: number): number {
  return prevContinuous + signedDelta(prevContinuous, nextBearing);
}

const fmt = (n: number): string => (Math.round(n * 100) / 100).toString();

/**
 * SVG path of a circular arc from a0 to a1 (degrees, clockwise, 0 up),
 * stroke-ready (no closing). Sweeps ≥360° render as a full circle.
 */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const sweep = a1 - a0;
  if (Math.abs(sweep) >= 360) {
    const top = polarXY(cx, cy, r, 0);
    const bottom = polarXY(cx, cy, r, 180);
    return (
      `M${fmt(top.x)},${fmt(top.y)}` +
      `A${fmt(r)},${fmt(r)} 0 1 1 ${fmt(bottom.x)},${fmt(bottom.y)}` +
      `A${fmt(r)},${fmt(r)} 0 1 1 ${fmt(top.x)},${fmt(top.y)}`
    );
  }
  const start = polarXY(cx, cy, r, a0);
  const end = polarXY(cx, cy, r, a1);
  const largeArc = Math.abs(sweep) > 180 ? 1 : 0;
  const sweepFlag = sweep >= 0 ? 1 : 0;
  return (
    `M${fmt(start.x)},${fmt(start.y)}` +
    `A${fmt(r)},${fmt(r)} 0 ${largeArc} ${sweepFlag} ${fmt(end.x)},${fmt(end.y)}`
  );
}

/** Arc length in px of a sweep at radius r. */
export function arcLength(r: number, sweepDeg: number): number {
  return (Math.abs(sweepDeg) * Math.PI * r) / 180;
}
